<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <SettingsNav />
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Lead Status Settings -->
        <div class="bg-white dark:bg-[#0c1427] p-5 rounded-md shadow-md flex flex-col min-h-[450px]">
          <h6 class="text-black dark:text-white font-semibold mb-6 text-center text-xl">{{ directT('settings.leadsSettings.statusManagement.title') }}</h6>
          
          <div class="mb-5 flex-grow">
            <div v-if="statusList.length === 0" class="text-gray-500 italic">
              {{ directT('settings.leadsSettings.statusManagement.noStatuses') }}
            </div>
            
            <ul class="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              <li 
                v-for="(status, index) in statusList" 
                :key="index"
                class="flex items-center justify-between bg-white dark:bg-[#0c1427] p-3 rounded border border-gray-200 dark:border-[#1c2b50]"
              >
                <div class="flex items-center gap-2">
                  <span 
                    class="w-3 h-3 rounded-full" 
                    :style="{ backgroundColor: status.color }"
                  ></span>
                  <span class="text-black dark:text-white">{{ status.name }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <button 
                    @click="editStatus(index)" 
                    class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <i class="material-symbols-outlined !text-md">edit</i>
                  </button>
                  <button 
                    @click="deleteStatus(index)" 
                    class="text-red-500 hover:text-red-700"
                  >
                    <i class="material-symbols-outlined !text-md">delete</i>
                  </button>
                </div>
              </li>
            </ul>
          </div>
          
          <div class="flex justify-between flex-nowrap gap-2 w-full mt-auto">
            <button 
              @click="addNewStatus" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md flex items-center justify-center whitespace-nowrap min-w-[120px]"
            >
              <span>{{ directT('settings.leadsSettings.statusManagement.addStatus') }}</span>
            </button>
            
            <button 
              @click="saveStatusSettings" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md whitespace-nowrap min-w-[120px] flex items-center justify-center"
              :disabled="saving"
            >
              {{ saving ? directT('settings.leadsSettings.saving') : directT('settings.leadsSettings.saveChanges') }}
            </button>
          </div>
        </div>
        
        <!-- Lead Source Settings -->
        <div class="bg-white dark:bg-[#0c1427] p-5 rounded-md shadow-md flex flex-col min-h-[450px]">
          <h6 class="text-black dark:text-white font-semibold mb-6 text-center text-xl">{{ directT('settings.leadsSettings.sourceManagement.title') }}</h6>
          
          <div class="mb-5 flex-grow">
            <div v-if="sourceList.length === 0" class="text-gray-500 italic">
              {{ directT('settings.leadsSettings.sourceManagement.noSources') }}
            </div>
            
            <ul class="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              <li 
                v-for="(source, index) in sourceList" 
                :key="index"
                class="flex items-center justify-between bg-white dark:bg-[#0c1427] p-3 rounded border border-gray-200 dark:border-[#1c2b50]"
              >
                <span class="text-black dark:text-white">{{ source.name }}</span>
                <div class="flex items-center gap-2">
                  <button 
                    @click="editSource(index)" 
                    class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <i class="material-symbols-outlined !text-md">edit</i>
                  </button>
                  <button 
                    @click="deleteSource(index)" 
                    class="text-red-500 hover:text-red-700"
                  >
                    <i class="material-symbols-outlined !text-md">delete</i>
                  </button>
                </div>
              </li>
            </ul>
          </div>
          
          <div class="flex justify-between flex-nowrap gap-2 w-full mt-auto">
            <button 
              @click="addNewSource" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md flex items-center justify-center whitespace-nowrap min-w-[120px]"
            >
              <span>{{ directT('settings.leadsSettings.sourceManagement.addSource') }}</span>
            </button>
            
            <button 
              @click="saveSourceSettings" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md whitespace-nowrap min-w-[120px] flex items-center justify-center"
              :disabled="saving"
            >
              {{ saving ? directT('settings.leadsSettings.saving') : directT('settings.leadsSettings.saveChanges') }}
            </button>
          </div>
        </div>
        
        <!-- Custom Fields Settings -->
        <div class="md:col-span-2 lg:col-span-1">
          <CustomFields />
        </div>
      </div>
    </div>
    
    <!-- Status Edit Modal -->
    <div v-if="showStatusModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black opacity-50" @click="closeStatusModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-black dark:text-white">{{ editingStatusIndex === -1 ? directT('settings.leadsSettings.statusManagement.addStatusTitle') : directT('settings.leadsSettings.statusManagement.editStatusTitle') }}</h3>
          <button 
            @click="closeStatusModal" 
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
        
        <form @submit.prevent="saveStatus" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('settings.leadsSettings.statusManagement.statusName') }}</label>
            <input 
              type="text" 
              v-model="editingStatus.name" 
              required
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
              :placeholder="directT('settings.leadsSettings.statusManagement.statusNamePlaceholder')"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('settings.leadsSettings.statusManagement.statusColor') }}</label>
            <input 
              type="color" 
              v-model="editingStatus.color" 
              class="w-full h-10 p-1 rounded border border-gray-200 dark:border-[#172036]"
            />
          </div>
          
          <div class="flex justify-end gap-2 pt-2">
            <button 
              type="button" 
              @click="closeStatusModal"
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
            >
              {{ directT('settings.leadsSettings.cancel') }}
            </button>
            <button 
              type="submit" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all"
            >
              {{ directT('settings.leadsSettings.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Source Edit Modal -->
    <div v-if="showSourceModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black opacity-50" @click="closeSourceModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-black dark:text-white">{{ editingSourceIndex === -1 ? directT('settings.leadsSettings.sourceManagement.addSourceTitle') : directT('settings.leadsSettings.sourceManagement.editSourceTitle') }}</h3>
          <button 
            @click="closeSourceModal" 
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
        
        <form @submit.prevent="saveSource" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{{ directT('settings.leadsSettings.sourceManagement.sourceName') }}</label>
            <input 
              type="text" 
              v-model="editingSource.name" 
              required
              class="bg-gray-50 border border-gray-200 rounded-md w-full px-3 py-2.5 text-black dark:bg-[#15203c] dark:text-white dark:border-[#172036]"
              :placeholder="directT('settings.leadsSettings.sourceManagement.sourceNamePlaceholder')"
            />
          </div>
          
          <div class="flex justify-end gap-2 pt-2">
            <button 
              type="button" 
              @click="closeSourceModal"
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
            >
              {{ directT('settings.leadsSettings.cancel') }}
            </button>
            <button 
              type="submit" 
              class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all"
            >
              {{ directT('settings.leadsSettings.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-black opacity-50" @click="closeDeleteModal"></div>
      <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-4 z-10 relative">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium text-black dark:text-white">{{ directT('settings.leadsSettings.confirmDeletion') }}</h3>
          <button 
            @click="closeDeleteModal" 
            class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
          >
            <i class="material-symbols-outlined text-xl">close</i>
          </button>
        </div>
        
        <div class="mb-6">
          <p class="text-gray-700 dark:text-gray-300">
            {{ directT(`settings.leadsSettings.${deleteType}Management.deleteConfirmation`) }}
          </p>
        </div>
        
        <div class="flex justify-between pt-2">
          <button 
            type="button" 
            @click="closeDeleteModal"
            class="inline-block py-2 px-4 bg-gray-100 text-gray-800 transition-all hover:bg-gray-200 rounded-md border border-gray-100 hover:border-gray-200 dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746] dark:border-[#172036] dark:hover:border-[#1e2746]"
          >
            {{ directT('settings.leadsSettings.cancel') }}
          </button>
          <button 
            type="button"
            @click="confirmDelete"
            class="inline-block py-2 px-4 bg-danger-500 text-white transition-all hover:bg-danger-400 rounded-md border border-danger-500 hover:border-danger-400"
          >
            {{ directT('settings.leadsSettings.delete') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Toast Notification -->
    <div 
      v-if="toastMessage" 
      class="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-md p-4 rounded-md shadow-lg transform transition-transform duration-300 z-50 flex items-center justify-between"
      :class="{ 
        'bg-green-600 text-white': toastType === 'success', 
        'bg-red-600 text-white': toastType === 'error', 
        'bg-blue-600 text-white': toastType === 'info' 
      }"
    >
      <span>{{ toastMessage }}</span>
      <button @click="closeToast" class="ml-4 text-white hover:text-gray-300">×</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import SettingsNav from "../SettingsNav.vue";
import CustomFields from "./CustomFields/index.vue";
import { createToast } from "@/utils/toast";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

// Store direct references to translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

interface StatusLeadRecord {
  id: string;
  status: string;
  color: string;
  organization_id: string;
  created_at?: string;
  updated_at?: string;
}

interface Status {
  id?: string;
  name: string;
  color: string;
  organization_id?: string;
}

interface LeadSourceRecord {
  id: string;
  source: string;
  organization_id: string;
  created_at?: string;
  updated_at?: string;
}

interface Source {
  id?: string;
  name: string;
  organization_id?: string;
}

export default defineComponent({
  name: "settings.leadsSettings",
  components: {
    SettingsNav,
    CustomFields,
  },
  setup() {
    const statusList = ref<Status[]>([]);
    const sourceList = ref<Source[]>([]);
    const saving = ref(false);
    const organizationId = ref<string | null>(null);
    
    const { locale } = useI18n();
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params: Record<string, any> = {}): string => {
      // Normalize keys that start with 'settings.leadsSettings'
      if (key.startsWith('settings.leadsSettings')) {
        key = key.replace('settings.leadsSettings', 'settings.leadsSettings');
      }
      
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      if (typeof result !== 'string') {
        console.warn(`Translation key ${key} does not resolve to a string`);
        return key;
      }
      
      // Handle parameter substitution if needed
      if (Object.keys(params).length > 0) {
        let finalResult: string = result;
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          finalResult = finalResult.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
        });
        return finalResult;
      }
      
      return result;
    };
    
    // Status modal
    const showStatusModal = ref(false);
    const editingStatus = ref<Status>({ name: '', color: '#605DFF' });
    const editingStatusIndex = ref(-1);
    
    // Source modal
    const showSourceModal = ref(false);
    const editingSource = ref<Source>({ name: '' });
    const editingSourceIndex = ref(-1);
    
    // Delete confirmation modal
    const showDeleteModal = ref(false);
    const deleteType = ref<'status' | 'source'>('status');
    const deleteIndex = ref<number>(-1);
    
    // Toast notification
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<number | null>(null);
    
    // Fetch settings on component mount
    onMounted(async () => {
      try {
        // Force reload language if needed
        if (localStorage.getItem('language') === 'he') {
          console.log('Detected Hebrew language, making sure translations are loaded');
          // Force a small delay to make sure everything is loaded
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          createToast('User is not associated with an organization', 'error');
          return;
        }
        
        await fetchLeadSettings();
      } catch (error) {
        console.error('Error loading lead settings:', error);
        createToast('Failed to load lead settings', 'error');
      }
    });
    
    // Fetch lead settings from the database
    const fetchLeadSettings = async () => {
      try {
        // Fetch lead statuses from statusleads table
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (statusError) throw statusError;
        
        // Map the database records to the Status interface with proper type assertion
        statusList.value = (statusData || []).map((item: any) => ({
          id: item.id as string,
          name: item.status as string,
          color: (item.color as string) || '#605DFF', // Default color if not provided
          organization_id: item.organization_id as string
        }));
        
        // If no statuses exist, create default ones
        if (statusList.value.length === 0) {
          await createDefaultStatuses();
        }
        
        // Fetch lead sources from leadsources table
        const { data: sourceData, error: sourceError } = await supabase
          .from('leadsources')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (sourceError) throw sourceError;
        
        // Map the database records to the Source interface
        sourceList.value = (sourceData || []).map((item: any) => ({
          id: item.id as string,
          name: item.source as string,
          organization_id: item.organization_id as string
        }));
        
        // If no sources exist, create default ones
        if (sourceList.value.length === 0) {
          await createDefaultSources();
        }
      } catch (error) {
        console.error('Error fetching lead settings:', error);
        createToast('Failed to fetch lead settings', 'error');
      }
    };
    
    // Create default statuses if none exist
    const createDefaultStatuses = async () => {
      try {
        const defaultStatuses = [
          { name: 'New', color: '#605DFF' },
          { name: 'In Progress', color: '#10B981' },
          { name: 'Pending', color: '#F59E0B' },
          { name: 'Rejected', color: '#EF4444' },
          { name: 'Close', color: '#10B981' }
        ];
        
        // Insert default statuses into database
        const { data, error } = await supabase
          .from('statusleads')
          .insert(
            defaultStatuses.map(status => ({
              status: status.name,
              color: status.color,
              organization_id: organizationId.value
            }))
          )
          .select();
        
        if (error) throw error;
        
        // Add default statuses to local state with proper type assertion
        if (data) {
          statusList.value = (data || []).map((item: any) => ({
            id: item.id as string,
            name: item.status as string,
            color: item.color as string,
            organization_id: item.organization_id as string
          }));
        }
      } catch (error) {
        console.error('Error creating default statuses:', error);
        createToast('Failed to create default statuses', 'error');
      }
    };
    
    // Create default sources if none exist
    const createDefaultSources = async () => {
      try {
        const defaultSources = [
          { name: 'Website' },
          { name: 'Referral' },
          { name: 'Social Media' },
          { name: 'Direct' },
          { name: 'Email Campaign' }
        ];
        
        // Insert default sources into database
        const { data, error } = await supabase
          .from('leadsources')
          .insert(
            defaultSources.map(source => ({
              source: source.name,
              organization_id: organizationId.value
            }))
          )
          .select();
        
        if (error) throw error;
        
        // Add default sources to local state
        if (data) {
          sourceList.value = (data || []).map((item: any) => ({
            id: item.id as string,
            name: item.source as string,
            organization_id: item.organization_id as string
          }));
        }
      } catch (error) {
        console.error('Error creating default sources:', error);
        createToast('Failed to create default sources', 'error');
      }
    };
    
    // Status functions
    const addNewStatus = () => {
      editingStatus.value = { name: '', color: '#605DFF' };
      editingStatusIndex.value = -1;
      showStatusModal.value = true;
    };
    
    const editStatus = (index: number) => {
      editingStatus.value = { ...statusList.value[index] };
      editingStatusIndex.value = index;
      showStatusModal.value = true;
    };
    
    const deleteStatus = async (index: number) => {
      deleteType.value = 'status';
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    const closeStatusModal = () => {
      showStatusModal.value = false;
    };
    
    const saveStatus = async () => {
      try {
        if (editingStatusIndex.value === -1) {
          // Add new status to database
          const { data, error } = await supabase
            .from('statusleads')
            .insert({
              status: editingStatus.value.name,
              color: editingStatus.value.color,
              organization_id: organizationId.value
            })
            .select();
          
          if (error) throw error;
          
          // Add to local list with the database ID and proper type assertion
          if (data && data[0]) {
            const newStatus = data[0] as any;
            statusList.value.push({ 
              id: newStatus.id as string,
              name: newStatus.status as string,
              color: newStatus.color as string,
              organization_id: newStatus.organization_id as string
            });
          }
        } else {
          // Update existing status
          const statusToUpdate = statusList.value[editingStatusIndex.value];
          
          if (statusToUpdate.id) {
            // Update in database
            const { error } = await supabase
              .from('statusleads')
              .update({
                status: editingStatus.value.name,
                color: editingStatus.value.color
              })
              .eq('id', statusToUpdate.id);
            
            if (error) throw error;
          } else {
            // Status doesn't exist in database yet, create it
            const { data, error } = await supabase
              .from('statusleads')
              .insert({
                status: editingStatus.value.name,
                color: editingStatus.value.color,
                organization_id: organizationId.value
              })
              .select();
            
            if (error) throw error;
            
            if (data && data[0]) {
              const newStatus = data[0] as any;
              editingStatus.value.id = newStatus.id as string;
              editingStatus.value.organization_id = newStatus.organization_id as string;
            }
          }
          
          // Update in local list
          statusList.value[editingStatusIndex.value] = { ...editingStatus.value };
        }
        
        createToast('Status saved successfully', 'success');
        closeStatusModal();
      } catch (error) {
        console.error('Error saving status:', error);
        createToast('Failed to save status', 'error');
      }
    };
    
    const saveStatusSettings = async () => {
      saving.value = true;
      
      try {
        // No additional saving needed as each status is saved individually
        createToast('Status settings saved successfully', 'success');
      } catch (error) {
        console.error('Error saving status settings:', error);
        createToast('Failed to save status settings', 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Source functions
    const addNewSource = () => {
      editingSource.value = { name: '' };
      editingSourceIndex.value = -1;
      showSourceModal.value = true;
    };
    
    const editSource = (index: number) => {
      editingSource.value = { ...sourceList.value[index] };
      editingSourceIndex.value = index;
      showSourceModal.value = true;
    };
    
    const deleteSource = async (index: number) => {
      deleteType.value = 'source';
      deleteIndex.value = index;
      showDeleteModal.value = true;
    };
    
    const closeSourceModal = () => {
      showSourceModal.value = false;
    };
    
    const saveSource = async () => {
      try {
        if (editingSourceIndex.value === -1) {
          // Add new source to database
          const { data, error } = await supabase
            .from('leadsources')
            .insert({
              source: editingSource.value.name,
              organization_id: organizationId.value
            })
            .select();
          
          if (error) throw error;
          
          // Add to local list with the database ID
          if (data && data[0]) {
            const newSource = data[0] as any;
            sourceList.value.push({ 
              id: newSource.id as string,
              name: newSource.source as string,
              organization_id: newSource.organization_id as string
            });
          }
        } else {
          // Update existing source
          const sourceToUpdate = sourceList.value[editingSourceIndex.value];
          
          if (sourceToUpdate.id) {
            // Update in database
            const { error } = await supabase
              .from('leadsources')
              .update({
                source: editingSource.value.name
              })
              .eq('id', sourceToUpdate.id);
            
            if (error) throw error;
          } else {
            // Source doesn't exist in database yet, create it
            const { data, error } = await supabase
              .from('leadsources')
              .insert({
                source: editingSource.value.name,
                organization_id: organizationId.value
              })
              .select();
            
            if (error) throw error;
            
            if (data && data[0]) {
              const newSource = data[0] as any;
              editingSource.value.id = newSource.id as string;
              editingSource.value.organization_id = newSource.organization_id as string;
            }
          }
          
          // Update in local list
          sourceList.value[editingSourceIndex.value] = { ...editingSource.value };
        }
        
        createToast('Source saved successfully', 'success');
        closeSourceModal();
      } catch (error) {
        console.error('Error saving source:', error);
        createToast('Failed to save source', 'error');
      }
    };
    
    const saveSourceSettings = async () => {
      saving.value = true;
      
      try {
        // No additional saving needed as each source is saved individually
        createToast('Source settings saved successfully', 'success');
      } catch (error) {
        console.error('Error saving source settings:', error);
        createToast('Failed to save source settings', 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Delete confirmation modal functions
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deleteIndex.value = -1;
    };
    
    const confirmDelete = async () => {
      if (deleteType.value === 'status') {
        await performDeleteStatus(deleteIndex.value);
      } else {
        await performDeleteSource(deleteIndex.value);
      }
      closeDeleteModal();
    };
    
    const performDeleteStatus = async (index: number) => {
      const statusToDelete = statusList.value[index];
      
      // If the status has an ID, it exists in the database and needs to be deleted
      if (statusToDelete.id) {
        try {
          const { error } = await supabase
            .from('statusleads')
            .delete()
            .eq('id', statusToDelete.id);
          
          if (error) throw error;
          
          // Remove from local list
          statusList.value.splice(index, 1);
          createToast('Status deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting status:', error);
          createToast('Failed to delete status', 'error');
        }
      } else {
        // Status was only local, just remove from list
        statusList.value.splice(index, 1);
        createToast('Status deleted successfully', 'success');
      }
    };
    
    const performDeleteSource = async (index: number) => {
      const sourceToDelete = sourceList.value[index];
      
      // If the source has an ID, it exists in the database and needs to be deleted
      if (sourceToDelete.id) {
        try {
          const { error } = await supabase
            .from('leadsources')
            .delete()
            .eq('id', sourceToDelete.id);
          
          if (error) throw error;
          
          // Remove from local list
          sourceList.value.splice(index, 1);
          createToast('Source deleted successfully', 'success');
        } catch (error) {
          console.error('Error deleting source:', error);
          createToast('Failed to delete source', 'error');
        }
      } else {
        // Source was only local, just remove from list
        sourceList.value.splice(index, 1);
        createToast('Source deleted successfully', 'success');
      }
    };
    
    // Toast functions
    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      toastTimeout.value = window.setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    const closeToast = () => {
      toastMessage.value = '';
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    return {
      statusList,
      sourceList,
      saving,
      
      // Status modal
      showStatusModal,
      editingStatus,
      editingStatusIndex,
      addNewStatus,
      editStatus,
      deleteStatus,
      closeStatusModal,
      saveStatus,
      saveStatusSettings,
      
      // Source modal
      showSourceModal,
      editingSource,
      editingSourceIndex,
      addNewSource,
      editSource,
      deleteSource,
      closeSourceModal,
      saveSource,
      saveSourceSettings,
      
      // Delete confirmation modal
      showDeleteModal,
      deleteType,
      closeDeleteModal,
      confirmDelete,
      
      // Toast
      toastMessage,
      toastType,
      closeToast,
      
      // Translation
      directT
    };
  },
});
</script>

<style scoped>
/* Custom scrollbar styles */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Dark mode scrollbar */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style> 